import { useState, useRef, useEffect } from "react";
import { IComentario, comentarioService } from "../../../../services/comentarioService";
import { CardWrapper, Container, TextareaEditavel, BotoesEdicao, BotaoSalvar, BotaoCancelar, IndicadorEditado, RespostasContainer, AcaoButton, TooltipContainer, Tooltip, MenuContainer, MenuButton, MenuDropdown, MenuItem, BotaoResponder, BadgeTipoDestinatario } from "./styles";
import { useTheme } from "styled-components";
import RespostasRecursivas from "../RespostasRecursivas";
import { Reply, Trash2, Pencil, MoreVertical, ChevronDown, ChevronUp } from "lucide-react";
import ModalDefault from "../../../ModalDefault";
import ButtonDefault from "../../../form/ButtonDefault";
import { ModalWrapper, ModalTitle, ModalButtons } from "../../../../pages/Demanda/styles";
import { IconClose } from "../../../icons";
import toast from "react-hot-toast";
import { toastDefsError, toastDefs } from "../../../../config/toastConfig";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import FormResposta from "../FormResposta";
import { getTipoLabelDestinatario } from "../../../../utility/comentarioTipos";

interface CardComentarioProps {
    comentario: IComentario;
    nivel?: number;
    id_proposta: number;
    onRespostaCriada: () => void;
    onComentarioAtualizado?: () => void;
    onIniciarResposta?: (id_comentario: number) => void;
    comentarioRespondendo?: number | null;
    idComentarioPaiParaResposta?: number | null;
    onCancelarResposta?: () => void;
    todosComentarios?: IComentario[];
    user: {
        tipo: string;
        id?: string | number;
        id_operador?: number;
        id_usuario?: number;
        id_solicitante?: number;
        id_escritorio_solicitante?: number;
    };
    demanda?: any;
}

function getIniciais(nome: string): string {
    if (!nome) return "?";
    const partes = nome.trim().split(" ");
    if (partes.length === 1) {
        return partes[0].substring(0, 2).toUpperCase();
    }
    return (partes[0].substring(0, 1) + partes[partes.length - 1].substring(0, 1)).toUpperCase();
}

function contarRespostasRecursivas(comentario: IComentario): number {
    if (!comentario.respostas || comentario.respostas.length === 0) {
        return 0;
    }
    let total = comentario.respostas.length;
    comentario.respostas.forEach(resposta => {
        total += contarRespostasRecursivas(resposta);
    });
    return total;
}

function encontrarComentarioPorId(id_comentario: number, comentarios: IComentario[]): IComentario | null {
    for (const comentario of comentarios) {
        if (comentario.id_comentario === id_comentario) {
            return comentario;
        }
        if (comentario.respostas && comentario.respostas.length > 0) {
            const encontrado = encontrarComentarioPorId(id_comentario, comentario.respostas);
            if (encontrado) {
                return encontrado;
            }
        }
    }
    return null;
}

function verificarThreadDesvinculada(comentario: IComentario, todosComentarios: IComentario[]): boolean {
    const comentarioPrincipal = comentario.id_comentario_pai 
        ? encontrarComentarioPorId(comentario.id_comentario_pai, todosComentarios) || comentario
        : comentario;
    
    if ((comentarioPrincipal as any).correspondentDesvinculado === true) {
        return true;
    }
    
    const verificarRespostas = (coment: IComentario): boolean => {
        if ((coment as any).correspondentDesvinculado === true) {
            return true;
        }
        if (coment.respostas && coment.respostas.length > 0) {
            return coment.respostas.some(resp => verificarRespostas(resp));
        }
        return false;
    };
    
    return verificarRespostas(comentarioPrincipal);
}

export default function CardComentario({ 
    comentario, 
    nivel = 0,
    id_proposta,
    onRespostaCriada,
    onComentarioAtualizado,
    onIniciarResposta,
    comentarioRespondendo,
    idComentarioPaiParaResposta,
    onCancelarResposta,
    todosComentarios = [],
    user,
    demanda
}: CardComentarioProps) {
    const theme = useTheme() as any;
    const [isEditando, setIsEditando] = useState(false);
    const [textoEditado, setTextoEditado] = useState(comentario.texto);
    const [isLoading, setIsLoading] = useState(false);
    const [erroTexto, setErroTexto] = useState("");
    const [mostrarConfirmacaoExclusao, setMostrarConfirmacaoExclusao] = useState(false);
    const [mostrarConfirmacaoCascata, setMostrarConfirmacaoCascata] = useState(false);
    const [tooltipAtivo, setTooltipAtivo] = useState<string | null>(null);
    const [menuAberto, setMenuAberto] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
    const [isTextoExpandido, setIsTextoExpandido] = useState(false);
    const [precisaTruncar, setPrecisaTruncar] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const textoRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const isMountedRef = useRef(true);

    const textoAlterado = textoEditado.trim() !== comentario.texto.trim();

    useEffect(() => {
        setTextoEditado(comentario.texto);
        
        return () => {
            isMountedRef.current = false;
        };
    }, [comentario.texto]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuAberto(false);
                setMenuPosition(null);
            }
        };

        if (menuAberto) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuAberto]);
    
    const tipoLabel = getTipoLabelDestinatario(comentario.tipo_responsavel);
    const iniciais = getIniciais(comentario.autor_nome || '');
    const isResposta = nivel > 0;
    const correspondentDesvinculado = (comentario as any).correspondentDesvinculado === true;
    const threadDesvinculada = verificarThreadDesvinculada(comentario, todosComentarios);

    const isOperador = user.tipo === 'Operador' || user.tipo === 'Admin';
    const isAutor = (() => {
        if (comentario.tipo_responsavel === 'Solicitante' && user.id_escritorio_solicitante) {
            return comentario.id_responsavel === user.id_escritorio_solicitante;
        }
        if (comentario.tipo_responsavel === 'Profissional' && user.id_usuario) {
            return comentario.id_responsavel === Number(user.id_usuario);
        }
        if (comentario.tipo_responsavel === 'Operador' && user.id_operador) {
            return comentario.id_responsavel === Number(user.id_operador);
        }
        return false;
    })();

    const podeEditar = isAutor && !threadDesvinculada;
    const podeExcluir = (isAutor || isOperador) && !threadDesvinculada;

    const podeResponderComentario = (): boolean => {
        if (threadDesvinculada) {
            return false;
        }

        if (isOperador) {
            return true;
        }

        if (comentario.tipo_responsavel === 'Operador') {
            return true;
        }

        return false;
    };

    const podeResponder = podeResponderComentario();

    // Verificar se o texto precisa ser truncado (mais de 10 linhas)
    useEffect(() => {
        if (textoRef.current && !isEditando) {
            // Usar setTimeout para garantir que o DOM foi renderizado
            const timeoutId = setTimeout(() => {
                if (textoRef.current) {
                    const elemento = textoRef.current;
                    const alturaReal = elemento.scrollHeight;
                    const alturaLinha = parseFloat(window.getComputedStyle(elemento).lineHeight) || 21; // 14px * 1.5
                    const alturaMaxima = alturaLinha * 10; // 10 linhas
                    
                    setPrecisaTruncar(alturaReal > alturaMaxima);
                }
            }, 0);
            
            return () => clearTimeout(timeoutId);
        }
    }, [comentario.texto, comentario.id_comentario, isEditando]);

    // Resetar expansão quando o comentário mudar
    useEffect(() => {
        setIsTextoExpandido(false);
    }, [comentario.id_comentario]);

    const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
        textarea.style.height = 'auto';
        const maxHeight = 300;
        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;
    };

    useEffect(() => {
        if (isEditando && textareaRef.current) {
            autoResizeTextarea(textareaRef.current);
        } else if (!isEditando) {
            setTooltipAtivo(null);
        }
    }, [isEditando, textoEditado]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuAberto(false);
            }
        };

        if (menuAberto) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuAberto]);

    const formatarTimestampRelativo = (data: string): string => {
        try {
            const dataComentario = new Date(data);
            if (Number.isNaN(dataComentario.getTime())) {
                return 'data inválida';
            }
            return formatDistanceToNow(dataComentario, { 
                addSuffix: true, 
                locale: ptBR 
            });
        } catch (error) {
            console.error('Erro ao formatar timestamp:', error);
            return 'data inválida';
        }
    };


    const handleEditar = () => {
        setTextoEditado(comentario.texto);
        setErroTexto("");
        setIsEditando(true);
        
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(
                    textareaRef.current.value.length,
                    textareaRef.current.value.length
                );
            }
        }, 0);
    };

    const handleCancelarEdicao = () => {
        setTextoEditado(comentario.texto);
        setErroTexto("");
        setIsEditando(false);
        setTooltipAtivo(null);
    };

    const handleSalvarEdicao = async () => {
        if (isLoading) return;
        
        if (!textoEditado.trim()) {
            setErroTexto("O comentário não pode estar vazio");
            return;
        }
        
        setErroTexto("");

        setIsLoading(true);
        try {
            await comentarioService.updateComentario(comentario.id_comentario!, textoEditado.trim());
            
            if (!isMountedRef.current) return;
            
            toast.success("Comentário atualizado com sucesso!", toastDefs);
            setIsEditando(false);
            setTooltipAtivo(null);
            onComentarioAtualizado?.();
        } catch (error: any) {
            if (!isMountedRef.current) return;
            
            console.error('Erro ao atualizar comentário:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao atualizar comentário';
            toast.error(errorMessage, toastDefsError);
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    const handleExcluir = () => {
        const quantidadeRespostas = contarRespostasRecursivas(comentario);
        if (quantidadeRespostas > 0) {
            setMostrarConfirmacaoCascata(true);
        } else {
            setMostrarConfirmacaoExclusao(true);
        }
    };

    const handleConfirmarExclusao = async () => {
        setIsLoading(true);
        try {
            const resultado = await comentarioService.deleteComentario(comentario.id_comentario!);
            
            if (!isMountedRef.current) return;
            
            const mensagem = resultado.quantidade_deletada > 1
                ? `${resultado.quantidade_deletada} comentários excluídos com sucesso`
                : "Comentário excluído com sucesso";
            toast.success(mensagem, toastDefs);
            setMostrarConfirmacaoExclusao(false);
            setMostrarConfirmacaoCascata(false);
            onComentarioAtualizado?.();
        } catch (error: any) {
            if (!isMountedRef.current) return;
            
            console.error('Erro ao excluir comentário:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao excluir comentário';
            toast.error(errorMessage, toastDefsError);
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    const handleCancelarExclusao = () => {
        setMostrarConfirmacaoExclusao(false);
        setMostrarConfirmacaoCascata(false);
    };

    const quantidadeRespostas = contarRespostasRecursivas(comentario);
    const quantidadeTotal = 1 + quantidadeRespostas;
    const tituloModalExclusao = mostrarConfirmacaoCascata 
        ? 'Excluir esta sequência de comentários?' 
        : 'Deseja excluir o comentario?';
    const descricaoPrincipalExclusao = mostrarConfirmacaoCascata
        ? `${quantidadeTotal} comentário${quantidadeTotal > 1 ? 's serão' : ' será'} excluído${quantidadeTotal > 1 ? 's' : ''}.`
        : '';
    const textoIrreversivel = 'Esta ação não pode ser desfeita.';
    const textoBotaoConfirmar = mostrarConfirmacaoCascata ? 'Excluir comentários' : 'Excluir comentário';
    const corDivisoriaModal = theme?.colors?.interfaceBorder100 || '#E5E7EB';

    const modalConfirmacao = (
        <ModalDefault
            id="modal-confirmacao-exclusao-comentario"
            onClose={handleCancelarExclusao}
            openModal={mostrarConfirmacaoExclusao || mostrarConfirmacaoCascata}
            width="520px"
            maxWidth="95vw"
        >
            <ModalWrapper>
                <ModalTitle>
                    <div className="points">
                        {tituloModalExclusao}
                    </div>
                    <div
                        className="close"
                        onClick={handleCancelarExclusao}
                    >
                        <IconClose />
                    </div>
                </ModalTitle>

                <div style={{ width: '100%', height: '1px', backgroundColor: corDivisoriaModal }} />

                <div className="accept">
                    {descricaoPrincipalExclusao && <p>{descricaoPrincipalExclusao}</p>}
                    <p>{textoIrreversivel}</p>
                </div>

                <ModalButtons>
                    <ButtonDefault
                        className="cancel"
                        onClick={handleCancelarExclusao}
                        disabled={isLoading}
                    >
                        Cancelar
                    </ButtonDefault>
                    <ButtonDefault
                        className="danger"
                        onClick={handleConfirmarExclusao}
                        disabled={isLoading}
                    >
                        {textoBotaoConfirmar}
                    </ButtonDefault>
                </ModalButtons>
            </ModalWrapper>
        </ModalDefault>
    );

    if (isResposta) {
        return (
            <>
            <Container 
                isResposta={true}
                nivel={nivel}
                data-comentario-id={comentario.id_comentario}
            >
                <div className="linha-principal">
                    <div className="avatar">
                        <div className="avatar-inner">
                            {iniciais}
                        </div>
                    </div>
                    <div className="conteudo">
                    <div className="header">
                        <div className="autor-container">
                            <div className="autor-info">
                                <span className="autor-nome">{comentario.autor_nome}</span>
                                <span className="tipo-badge">
                                    {tipoLabel}
                                </span>
                                {correspondentDesvinculado && (
                                    <span className="badge-desvinculado">
                                        Desvinculado
                                    </span>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: 0, margin: 0 }}>
                            <div className="timestamp">
                                {formatarTimestampRelativo(comentario.criado)}
                            </div>
                            {comentario.id_comentario && (
                                <span style={{ 
                                    color: 'var(--c64748b)', 
                                    fontSize: '11px',
                                    fontWeight: 'normal'
                                }}>
                                    #{comentario.id_comentario}
                                </span>
                            )}
                            {!isEditando && (podeEditar || podeExcluir) && (
                                <MenuContainer ref={menuRef}>
                                    <MenuButton
                                        ref={menuButtonRef}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (menuButtonRef.current) {
                                                const rect = menuButtonRef.current.getBoundingClientRect();
                                                setMenuPosition({
                                                    top: rect.bottom + 8,
                                                    right: window.innerWidth - rect.right
                                                });
                                            }
                                            setMenuAberto(!menuAberto);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (menuButtonRef.current) {
                                                    const rect = menuButtonRef.current.getBoundingClientRect();
                                                    setMenuPosition({
                                                        top: rect.bottom + 8,
                                                        right: window.innerWidth - rect.right
                                                    });
                                                }
                                                setMenuAberto(!menuAberto);
                                            }
                                        }}
                                        aria-label="Menu de ações"
                                    >
                                        <MoreVertical size={18} />
                                    </MenuButton>
                                    {menuAberto && menuPosition && (
                                        <MenuDropdown style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}>
                                            {podeEditar && (
                                                <MenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setMenuAberto(false);
                                                        setMenuPosition(null);
                                                        handleEditar();
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setMenuAberto(false);
                                                            handleEditar();
                                                        }
                                                    }}
                                                >
                                                    <Pencil size={16} />
                                                    Editar
                                                </MenuItem>
                                            )}
                                            {podeExcluir && (
                                                <MenuItem
                                                    className="delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setMenuAberto(false);
                                                        setMenuPosition(null);
                                                        handleExcluir();
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setMenuAberto(false);
                                                            handleExcluir();
                                                        }
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                    Excluir
                                                </MenuItem>
                                            )}
                                        </MenuDropdown>
                                    )}
                                </MenuContainer>
                            )}
                        </div>
                    </div>
                    </div>
                </div>
                <div className="conteudo corpo-conteudo resposta">
                    {isEditando ? (
                        <>
                            <div style={{ position: 'relative', width: '100%' }}>
                                <TextareaEditavel
                                    ref={textareaRef}
                                    value={textoEditado}
                                    onChange={(e) => {
                                        const novoTexto = e.target.value;
                                        setTextoEditado(novoTexto);
                                        autoResizeTextarea(e.target);
                                        
                                        if (erroTexto && novoTexto.trim()) {
                                            setErroTexto("");
                                        }
                                    }}
                                    placeholder="Digite seu comentário..."
                                    rows={1}
                                    aria-label="Campo de edição do comentário"
                                    aria-invalid={!!erroTexto}
                                    aria-describedby={erroTexto ? "erro-edicao" : undefined}
                                    style={{
                                        borderColor: erroTexto ? 'var(--cef4444)' : undefined
                                    }}
                                />
                                {erroTexto && (
                                    <div id="erro-edicao" style={{ padding: '4px 8px', color: 'var(--cef4444)', fontSize: '12px', marginTop: '4px' }} role="alert">
                                        {erroTexto}
                                    </div>
                                )}
                            </div>
                            <BotoesEdicao>
                                <BotaoCancelar 
                                    onClick={handleCancelarEdicao}
                                    disabled={isLoading}
                                >
                                    Cancelar
                                </BotaoCancelar>
                                <BotaoSalvar 
                                    onClick={handleSalvarEdicao}
                                    disabled={!textoEditado.trim() || isLoading || !textoAlterado}
                                >
                                    Salvar
                                </BotaoSalvar>
                            </BotoesEdicao>
                        </>
                    ) : (
                        <>
                            <div 
                                ref={textoRef}
                                className="texto"
                                style={{
                                    maxHeight: precisaTruncar && !isTextoExpandido ? '210px' : 'none',
                                    overflow: precisaTruncar && !isTextoExpandido ? 'hidden' : 'visible',
                                    position: 'relative'
                                }}
                            >
                                {comentario.texto.split('\n').map((linha, index, array) => (
                                    <span key={index}>
                                        {linha}
                                        {index < array.length - 1 && <br />}
                                    </span>
                                ))}
                                {precisaTruncar && !isTextoExpandido && (
                                    <div 
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            height: '60px',
                                            background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.95))',
                                            pointerEvents: 'none'
                                        }}
                                    />
                                )}
                            </div>
                            {precisaTruncar && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsTextoExpandido(!isTextoExpandido);
                                    }}
                                    className="expandir-texto-btn"
                                    aria-label={isTextoExpandido ? "Recolher texto" : "Expandir texto"}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--c2e86de)',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        padding: '6px 0',
                                        marginTop: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'color 0.2s ease',
                                        fontFamily: 'inherit'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#1E5FA8';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = '#2E86DE';
                                    }}
                                >
                                    {isTextoExpandido ? (
                                        <>
                                            <ChevronUp size={16} />
                                            Ver menos
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown size={16} />
                                            Ver mais
                                        </>
                                    )}
                                </button>
                            )}
                            {comentario.modificado && comentario.modificado !== null && comentario.modificado !== comentario.criado && (
                                <IndicadorEditado>
                                    (editado)
                                </IndicadorEditado>
                            )}
                            {podeResponder && !isEditando && (
                                <div className="acoes-container">
                                    <BotaoResponder
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (comentario.id_comentario && onIniciarResposta) {
                                                onIniciarResposta(comentario.id_comentario);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (comentario.id_comentario) {
                                                    onIniciarResposta?.(comentario.id_comentario);
                                                }
                                            }
                                        }}
                                        aria-label="Responder ao comentário"
                                    >
                                        <Reply size={16} />
                                        Responder
                                    </BotaoResponder>
                                </div>
                            )}
                        </>
                    )}
                    {correspondentDesvinculado && (
                        <div style={{ 
                            marginTop: '8px', 
                            padding: '8px', 
                            backgroundColor: 'rgb(254, 242, 242)', 
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: 'rgb(153, 27, 27)',
                            fontStyle: 'italic'
                        }}>
                            Este {comentario.tipo_responsavel === 'Solicitante' ? 'solicitante' : 'profissional'} foi desvinculado da demanda. Não é possível responder.
                        </div>
                    )}
                </div>
            </Container>
            {((comentario.respostas && comentario.respostas.length > 0) || comentarioRespondendo === comentario.id_comentario) && (
                <RespostasContainer>
                    {comentario.respostas && comentario.respostas.length > 0 && (
                        <RespostasRecursivas
                            respostas={comentario.respostas}
                            nivel={nivel + 1}
                            id_proposta={id_proposta}
                            onRespostaCriada={onRespostaCriada}
                            onComentarioAtualizado={onComentarioAtualizado}
                            onIniciarResposta={onIniciarResposta}
                            comentarioRespondendo={comentarioRespondendo}
                            onCancelarResposta={onCancelarResposta}
                            todosComentarios={todosComentarios}
                            user={user}
                            demanda={demanda}
                        />
                    )}
                    {comentarioRespondendo === comentario.id_comentario && onCancelarResposta && idComentarioPaiParaResposta && (() => {
                        const comentarioRespondido = encontrarComentarioPorId(idComentarioPaiParaResposta, todosComentarios);
                        // Se o usuário for Solicitante ou Correspondente, o destinatário sempre será "Operador" (sem nome específico)
                        const isSolicitanteOuCorrespondente = user.tipo === 'Solicitante' || user.tipo === 'Profissional' || user.tipo === 'Correspondente';
                        return (
                            <FormResposta
                                id_comentario_pai={idComentarioPaiParaResposta}
                                id_proposta={id_proposta}
                                onRespostaCriada={onRespostaCriada}
                                onCancelar={onCancelarResposta}
                                destinatarioNome={isSolicitanteOuCorrespondente ? null : (comentarioRespondido?.autor_nome || null)}
                                destinatarioTipo={isSolicitanteOuCorrespondente ? 'Operador' : (comentarioRespondido?.tipo_responsavel || null)}
                                userTipo={user.tipo}
                            />
                        );
                    })()}
                </RespostasContainer>
            )}

            {modalConfirmacao}
            </>
        );
    }

    return (
        <>
        <CardWrapper data-comentario-id={comentario.id_comentario}>
            <Container 
                isResposta={false}
            >
                <div className="linha-principal">
                    <div className="avatar">
                        <div className="avatar-inner">
                            {iniciais}
                        </div>
                    </div>
                    <div className="conteudo">
                        <div className="header">
                            <div className="autor-container">
                                <div className="autor-info">
                                    <span className="autor-nome">{comentario.autor_nome}</span>
                                <span className="tipo-badge">
                                        {tipoLabel}
                                    </span>
                                    {correspondentDesvinculado && (
                                    <span className="badge-desvinculado">
                                            Desvinculado
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: 0, margin: 0 }}>
                                <div className="timestamp">
                                    {formatarTimestampRelativo(comentario.criado)}
                                </div>
                                {comentario.id_comentario && (
                                    <span style={{ 
                                        color: 'var(--c64748b)', 
                                        fontSize: '11px',
                                        fontWeight: 'normal'
                                    }}>
                                        #{comentario.id_comentario}
                                    </span>
                                )}
                                {!isEditando && (podeEditar || podeExcluir) && (
                                    <MenuContainer ref={menuRef}>
                                        <MenuButton
                                            ref={menuButtonRef}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (menuButtonRef.current) {
                                                    const rect = menuButtonRef.current.getBoundingClientRect();
                                                    setMenuPosition({
                                                        top: rect.bottom + 8,
                                                        right: window.innerWidth - rect.right
                                                    });
                                                }
                                                setMenuAberto(!menuAberto);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (menuButtonRef.current) {
                                                        const rect = menuButtonRef.current.getBoundingClientRect();
                                                        setMenuPosition({
                                                            top: rect.bottom + 8,
                                                            right: window.innerWidth - rect.right
                                                        });
                                                    }
                                                    setMenuAberto(!menuAberto);
                                                }
                                            }}
                                            aria-label="Menu de ações"
                                        >
                                            <MoreVertical size={18} />
                                        </MenuButton>
                                        {menuAberto && menuPosition && (
                                            <MenuDropdown style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}>
                                                {podeEditar && (
                                                    <MenuItem
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMenuAberto(false);
                                                            setMenuPosition(null);
                                                            handleEditar();
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setMenuAberto(false);
                                                                handleEditar();
                                                            }
                                                        }}
                                                    >
                                                        <Pencil size={16} />
                                                        Editar
                                                    </MenuItem>
                                                )}
                                                {podeExcluir && (
                                                    <MenuItem
                                                        className="delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMenuAberto(false);
                                                            setMenuPosition(null);
                                                            handleExcluir();
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setMenuAberto(false);
                                                                handleExcluir();
                                                            }
                                                        }}
                                                    >
                                                        <Trash2 size={16} />
                                                        Excluir
                                                    </MenuItem>
                                                )}
                                            </MenuDropdown>
                                        )}
                                    </MenuContainer>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="conteudo corpo-conteudo">
                    {comentario.tipo_destinatario && (
                        <div className="destinatario-subtitulo">
                            Para: {comentario.tipo_destinatario === 'OperadoresInterno' 
                                ? 'Todos os Operadores (comunicação interna) '
                                : comentario.destinatario_nome ? `${comentario.destinatario_nome} ` : ''}
                            <BadgeTipoDestinatario>
                                {comentario.tipo_destinatario === 'OperadoresInterno' 
                                    ? 'Operador' 
                                    : getTipoLabelDestinatario(comentario.tipo_destinatario)}
                            </BadgeTipoDestinatario>
                        </div>
                    )}
                    {isEditando ? (
                        <>
                            <div style={{ position: 'relative', width: '100%' }}>
                                <TextareaEditavel
                                    ref={textareaRef}
                                    value={textoEditado}
                                    onChange={(e) => {
                                        const novoTexto = e.target.value;
                                        setTextoEditado(novoTexto);
                                        autoResizeTextarea(e.target);
                                        
                                        if (erroTexto && novoTexto.trim()) {
                                            setErroTexto("");
                                        }
                                    }}
                                    placeholder="Digite seu comentário..."
                                    rows={1}
                                    aria-label="Campo de edição do comentário"
                                    aria-invalid={!!erroTexto}
                                    aria-describedby={erroTexto ? "erro-edicao" : undefined}
                                    style={{
                                        borderColor: erroTexto ? 'var(--cef4444)' : undefined
                                    }}
                                />
                                {erroTexto && (
                                    <div id="erro-edicao" style={{ padding: '4px 8px', color: 'var(--cef4444)', fontSize: '12px', marginTop: '4px' }} role="alert">
                                        {erroTexto}
                                    </div>
                                )}
                            </div>
                            <BotoesEdicao>
                                <BotaoCancelar 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelarEdicao();
                                    }}
                                    disabled={isLoading}
                                >
                                    Cancelar
                                </BotaoCancelar>
                                <BotaoSalvar 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSalvarEdicao();
                                    }}
                                    disabled={!textoEditado.trim() || isLoading || !textoAlterado}
                                >
                                    Salvar
                                </BotaoSalvar>
                            </BotoesEdicao>
                        </>
                    ) : (
                        <>
                            <div 
                                ref={textoRef}
                                className="texto"
                                style={{
                                    maxHeight: precisaTruncar && !isTextoExpandido ? '210px' : 'none',
                                    overflow: precisaTruncar && !isTextoExpandido ? 'hidden' : 'visible',
                                    position: 'relative'
                                }}
                            >
                                {comentario.texto.split('\n').map((linha, index, array) => (
                                    <span key={index}>
                                        {linha}
                                        {index < array.length - 1 && <br />}
                                    </span>
                                ))}
                                {precisaTruncar && !isTextoExpandido && (
                                    <div 
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            height: '60px',
                                            background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.95))',
                                            pointerEvents: 'none'
                                        }}
                                    />
                                )}
                            </div>
                            {precisaTruncar && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsTextoExpandido(!isTextoExpandido);
                                    }}
                                    className="expandir-texto-btn"
                                    aria-label={isTextoExpandido ? "Recolher texto" : "Expandir texto"}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--c2e86de)',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        padding: '6px 0',
                                        marginTop: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'color 0.2s ease',
                                        fontFamily: 'inherit'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#1E5FA8';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = '#2E86DE';
                                    }}
                                >
                                    {isTextoExpandido ? (
                                        <>
                                            <ChevronUp size={16} />
                                            Ver menos
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown size={16} />
                                            Ver mais
                                        </>
                                    )}
                                </button>
                            )}
                            {comentario.modificado && comentario.modificado !== null && comentario.modificado !== comentario.criado && (
                                <IndicadorEditado>
                                    (editado)
                                </IndicadorEditado>
                            )}
                            {podeResponder && !isEditando && (
                                <div className="acoes-container">
                                    <BotaoResponder
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (comentario.id_comentario && onIniciarResposta) {
                                                onIniciarResposta(comentario.id_comentario);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (comentario.id_comentario) {
                                                    onIniciarResposta?.(comentario.id_comentario);
                                                }
                                            }
                                        }}
                                        aria-label="Responder ao comentário"
                                    >
                                        <Reply size={16} />
                                        Responder
                                    </BotaoResponder>
                                </div>
                            )}
                        </>
                    )}
                    {correspondentDesvinculado && (
                        <div style={{ 
                            marginTop: '8px', 
                            padding: '8px', 
                            backgroundColor: 'rgb(254, 242, 242)', 
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: 'rgb(153, 27, 27)',
                            fontStyle: 'italic'
                        }}>
                            Este {comentario.tipo_responsavel === 'Solicitante' ? 'solicitante' : 'profissional'} foi desvinculado da demanda. Não é possível responder.
                        </div>
                    )}
                </div>
            </Container>


            {((comentario.respostas && comentario.respostas.length > 0) || comentarioRespondendo === comentario.id_comentario) && (
                <RespostasContainer>
                    {comentario.respostas && comentario.respostas.length > 0 && (
                        <RespostasRecursivas
                            respostas={comentario.respostas}
                            nivel={nivel + 1}
                            id_proposta={id_proposta}
                            onRespostaCriada={onRespostaCriada}
                            onComentarioAtualizado={onComentarioAtualizado}
                            onIniciarResposta={onIniciarResposta}
                            comentarioRespondendo={comentarioRespondendo}
                            onCancelarResposta={onCancelarResposta}
                            todosComentarios={todosComentarios}
                            user={user}
                            demanda={demanda}
                        />
                    )}
                    {comentarioRespondendo === comentario.id_comentario && onCancelarResposta && idComentarioPaiParaResposta && (() => {
                        const comentarioRespondido = encontrarComentarioPorId(idComentarioPaiParaResposta, todosComentarios);
                        // Se o usuário for Solicitante ou Correspondente, o destinatário sempre será "Operador" (sem nome específico)
                        const isSolicitanteOuCorrespondente = user.tipo === 'Solicitante' || user.tipo === 'Profissional' || user.tipo === 'Correspondente';
                        return (
                            <FormResposta
                                id_comentario_pai={idComentarioPaiParaResposta}
                                id_proposta={id_proposta}
                                onRespostaCriada={onRespostaCriada}
                                onCancelar={onCancelarResposta}
                                destinatarioNome={isSolicitanteOuCorrespondente ? null : (comentarioRespondido?.autor_nome || null)}
                                destinatarioTipo={isSolicitanteOuCorrespondente ? 'Operador' : (comentarioRespondido?.tipo_responsavel || null)}
                                userTipo={user.tipo}
                            />
                        );
                    })()}
                </RespostasContainer>
            )}
        </CardWrapper>

        {modalConfirmacao}
        </>
    );
}
